import React, { useState, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Typography, IconButton, Avatar,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Button, TextField, Grid, MenuItem, Chip, Pagination, Skeleton, Drawer, Alert
    , Badge, Switch, Tooltip, Stack, Divider, InputAdornment, FormControl, Select
} from '@mui/material';
import { MdDeleteForever, MdEmail, MdPhone, MdOutlineAdminPanelSettings, MdBusiness, MdEdit, MdClear, MdOutlineSearch, MdVisibility } from "react-icons/md";
import { FaArrowLeft, FaEye } from 'react-icons/fa';
import { IoPersonAddSharp } from "react-icons/io5";
import { FiFilter as FilterIcon, FiSmartphone, FiMail, FiMapPin, FiCalendar, FiUser, FiInfo, FiActivity, FiShield, FiBriefcase } from 'react-icons/fi';
import { postFileUpload, getData, deleteData, putData } from '../../Webservice/Webservice';


function Customermanagement() {
    const [editMode, setEditMode] = useState(false);
    const [editCustomerId, setEditCustomerId] = useState(null);
    const [customerList, setCustomerList] = useState([]);
    const [filters, setFilters] = useState({
        name: '',
        mobile: '',
        email: '',
        gender: '',
        provider: '',
        customerType: '',
        businessType: '',
        status: '',
        isVerified: '',
        isDeleted: ''
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [addCustomerOpen, setAddCustomerOpen] = useState(false);
    const [viewCustomer, setViewCustomer] = useState(null);
    const [filterSearch, setFilterSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Form State
    const [newCustomer, setNewCustomer] = useState({
        name: '',
        email: '',
        password: '',
        provider: 'admin',
        isVerified: false,
        image: null,
        imagePreview: '',
        gender: 'male',
        mobile: '',
        customerType: 'customer'
    });

    // Validation & Errors
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');

    const PAGE_SIZE = 10;
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({
        name: '',
        mobile: '',
        email: '',
        gender: '',
        provider: '',
        customerType: '',
        businessType: '',
        status: '',
        isVerified: '',
        isDeleted: ''
    });
    const [loading, setLoading] = useState(true);

    // Fetch Customers
    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem('token') ? `Bearer ${sessionStorage.getItem('token')}` : '';
            const params = {
                page: meta.page,
                limit: PAGE_SIZE,
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
                ...(filterSearch ? { name: filterSearch } : {}),
                ...(filterStatus ? { status: filterStatus } : {}),
            };
            const response = await getData('customer/list', params, token);

            if (response && Array.isArray(response.customer)) {
                setCustomerList(response.customer);
                const m = response.meta || { page: 1, totalPages: 1, total: 0 };
                setMeta({ page: m.page, totalPages: m.totalPages, total: m.total });
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.page, filters, filterSearch, filterStatus]);

    // Local filtering removed in favor of server-side filtering

    // Validation
    const validateForm = () => {
        const newErrors = {};

        if (!newCustomer.name.trim()) newErrors.name = 'Name is required';
        if (!newCustomer.email.trim()) newErrors.email = 'Email is required';
        else if (!/^\S+@\S+\.\S+$/.test(newCustomer.email)) newErrors.email = 'Invalid email format';

        if (!editMode && !newCustomer.password) newErrors.password = 'Password is required';
        else if (!editMode && newCustomer.password.length < 6) newErrors.password = 'Password must be 6+ characters';

        if (!newCustomer.mobile.trim()) newErrors.mobile = 'Mobile number is required';
        else if (!/^\d{10}$/.test(newCustomer.mobile.replace(/\D/g, ''))) newErrors.mobile = 'Must be 10 digits';

        if (!editMode && !newCustomer.image) newErrors.image = 'Profile image is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Input Handler
    const handleInputChange = (e) => {
        const { name, value, files } = e.target;

        if (name === 'image' && files?.[0]) {
            const file = files[0];
            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({ ...prev, image: 'Please select a valid image' }));
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, image: 'Image must be under 5MB' }));
                return;
            }

            setNewCustomer(prev => ({
                ...prev,
                image: file,
                imagePreview: URL.createObjectURL(file)
            }));
            setErrors(prev => ({ ...prev, image: undefined }));
        } else {
            setNewCustomer(prev => ({ ...prev, [name]: value }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    // MAIN SUBMIT: Add or Update Customer
    const handleStatusToggle = async (customer) => {
        try {
            const token = sessionStorage.getItem('token') ? `Bearer ${sessionStorage.getItem('token')}` : '';
            const newStatus = customer.status === 'active' ? 'inactive' : 'active';
            await putData(`customer/status/${customer._id}`, { status: newStatus }, token);
            // Update view drawer immediately for responsiveness
            setViewCustomer(prev => prev && prev._id === customer._id ? { ...prev, status: newStatus } : prev);
            // Refresh the full list from server
            await fetchCustomers();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleVerifiedToggle = async (customer) => {
        try {
            const token = sessionStorage.getItem('token') ? `Bearer ${sessionStorage.getItem('token')}` : '';
            const newVerified = !customer.isVerified;
            await putData(`customer/status/${customer._id}`, { isVerified: newVerified }, token);
            // Update view drawer immediately for responsiveness
            setViewCustomer(prev => prev && prev._id === customer._id ? { ...prev, isVerified: newVerified } : prev);
            // Refresh the full list from server
            await fetchCustomers();
        } catch (error) {
            console.error('Error updating verification:', error);
        }
    };

    const clearFilters = () => {
        setFilterSearch('');
        setFilterStatus('');
        setFilters({
            name: '', mobile: '', email: '', gender: '', provider: '',
            customerType: '', businessType: '', status: '', isVerified: '', isDeleted: ''
        });
        setTempFilters({
            name: '', mobile: '', email: '', gender: '', provider: '',
            customerType: '', businessType: '', status: '', isVerified: '', isDeleted: ''
        });
        setMeta(prev => ({ ...prev, page: 1 }));
    };

    const handleAddCustomer = async () => {
        setFormError('');
        if (!validateForm()) {
            setFormError('Please fix the errors above.');
            return;
        }

        const token = sessionStorage.getItem('token') ? `Bearer ${sessionStorage.getItem('token')}` : '';

        try {
            let response;

            if (editMode && editCustomerId) {
                // === UPDATE MODE ===
                if (newCustomer.image instanceof File) {
                    // New image selected → use postFileUpload
                    const formData = new FormData();
                    formData.append('name', newCustomer.name);
                    formData.append('email', newCustomer.email);
                    formData.append('provider', newCustomer.provider);
                    formData.append('isVerified', newCustomer.isVerified);
                    formData.append('gender', newCustomer.gender);
                    formData.append('mobile', newCustomer.mobile);
                    formData.append('customerType', newCustomer.customerType);
                    formData.append('image', newCustomer.image);

                    response = await postFileUpload(`customer/update/${editCustomerId}`, formData, token);
                } else {
                    // No new image → keep old image, use putData (JSON)
                    const payload = {
                        name: newCustomer.name,
                        email: newCustomer.email,
                        provider: newCustomer.provider,
                        isVerified: newCustomer.isVerified,
                        gender: newCustomer.gender,
                        mobile: newCustomer.mobile,
                        customerType: newCustomer.customerType
                    };
                    response = await putData(`customer/update/${editCustomerId}`, payload, token);
                }
            } else {
                // === ADD NEW CUSTOMER ===
                const formData = new FormData();
                formData.append('name', newCustomer.name);
                formData.append('email', newCustomer.email);
                formData.append('password', newCustomer.password);
                formData.append('provider', newCustomer.provider);
                formData.append('isVerified', newCustomer.isVerified);
                formData.append('gender', newCustomer.gender);
                formData.append('mobile', newCustomer.mobile);
                formData.append('customerType', newCustomer.customerType);
                formData.append('image', newCustomer.image);

                response = await postFileUpload('customer/add', formData, token);
            }

            // Success → Refresh list
            const updated = await getData('customer/list', { page: meta.page, limit: PAGE_SIZE, ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')) }, token);
            if (updated?.customer) {
                setCustomerList(updated.customer);
            }
            handleCloseAddCustomer();

        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Operation failed';
            setFormError(msg);
        }
    };

    const handleCloseAddCustomer = () => {
        setAddCustomerOpen(false);
        setEditMode(false);
        setEditCustomerId(null);
        setNewCustomer({
            name: '', email: '', password: '', provider: 'admin',
            isVerified: false, image: null, imagePreview: '', gender: 'male', mobile: '',
            customerType: 'customer'
        });
        setErrors({});
        setFormError('');
    };

    const handleOpenEdit = (customer) => {
        setEditMode(true);
        setEditCustomerId(customer._id);
        setNewCustomer({
            name: customer.name || '',
            email: customer.email || '',
            password: '',
            provider: customer.provider || 'admin',
            isVerified: customer.isVerified || false,
            image: null,
            imagePreview: customer.image || '',
            gender: customer.gender || 'male',
            mobile: customer.mobile || '',
            customerType: customer.customerType || 'customer'
        });
        setErrors({});
        setFormError('');
        setAddCustomerOpen(true);
    };

    const handleDeleteClick = (customer) => {
        setCustomerToDelete(customer);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!customerToDelete) return;
        try {
            const token = sessionStorage.getItem('token') ? `Bearer ${sessionStorage.getItem('token')}` : '';
            await deleteData(`customer/delete/${customerToDelete._id}`, token);
            // Refresh the full list from server
            await fetchCustomers();
        } catch (err) { }
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
    };



    const highlightText = (text, highlight) => {
        if (!highlight || !highlight.trim() || !text) return text;
        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === highlight.toLowerCase() ?
                <span key={i} style={{ backgroundColor: 'yellow' }}>{part}</span> : part
        );
    };

    const handlePageChange = (e, page) => setMeta(prev => ({ ...prev, page }));

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: { xs: 1, md: 2 } }}>
            {/* ── Header ───────────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#111827' }}>
                        Customer Management
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', fontFamily: 'Mulish, sans-serif' }}>
                        {meta.total || customerList.length} total customers
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField size="small" placeholder="Search customers..." value={filterSearch}
                        onChange={e => setFilterSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><MdOutlineSearch size={18} color="#9ca3af" /></InputAdornment>
                        }}
                        sx={{
                            minWidth: 220, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem', borderRadius: 2, bgcolor: '#fff' }
                        }} />

                    <TextField select size="small" label="Status" value={filterStatus}
                        onChange={e => setFilterStatus(e.target.value)}
                        sx={{
                            minWidth: 140, '& .MuiInputBase-root': { fontFamily: 'Mulish, sans-serif', fontSize: '0.88rem', borderRadius: 2, bgcolor: '#fff' },
                            '& .MuiInputLabel-root': { fontFamily: 'Mulish, sans-serif' }
                        }}>
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="verificationPending">Pending</MenuItem>
                    </TextField>

                    {(filterSearch || filterStatus || Object.values(filters).some(x => x)) && (
                        <Button size="small" variant="outlined" startIcon={<MdClear size={14} />}
                            onClick={clearFilters}
                            sx={{
                                fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                                borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px',
                                '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }, height: '40px'
                            }}>
                            Clear
                        </Button>
                    )}

                    <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => {
                        setTempFilters(filters);
                        setFilterDrawerOpen(true);
                    }} sx={{
                        fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 600,
                        borderColor: '#e5e7eb', color: '#6b7280', borderRadius: '8px', height: '40px',
                        '&:hover': { bgcolor: '#f9fafb', borderColor: '#d1d5db' }, bgcolor: '#fff'
                    }}>
                        More Filters
                    </Button>
                </Box>
            </Box>

            {/* Filter Drawer */}
            <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
                <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>Filters</Typography>

                    <TextField fullWidth label="Name" size="small" value={tempFilters.name}
                        onChange={e => setTempFilters(prev => ({ ...prev, name: e.target.value }))} />

                    <TextField fullWidth label="Mobile" size="small" value={tempFilters.mobile}
                        onChange={e => setTempFilters(prev => ({ ...prev, mobile: e.target.value }))} />

                    <TextField fullWidth label="Email" size="small" value={tempFilters.email}
                        onChange={e => setTempFilters(prev => ({ ...prev, email: e.target.value }))} />

                    <TextField select fullWidth label="Gender" size="small" value={tempFilters.gender}
                        onChange={e => setTempFilters(prev => ({ ...prev, gender: e.target.value }))}>
                        <MenuItem value="">Any Gender</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Provider" size="small" value={tempFilters.provider}
                        onChange={e => setTempFilters(prev => ({ ...prev, provider: e.target.value }))}>
                        <MenuItem value="">Any Provider</MenuItem>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="mobile">Mobile</MenuItem>
                        <MenuItem value="google">Google</MenuItem>
                        <MenuItem value="facebook">Facebook</MenuItem>
                        <MenuItem value="admin">Admin</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Customer Type" size="small" value={tempFilters.customerType}
                        onChange={e => setTempFilters(prev => ({ ...prev, customerType: e.target.value }))}>
                        <MenuItem value="">Any Type</MenuItem>
                        <MenuItem value="customer">Customer</MenuItem>
                        <MenuItem value="business">Business</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Business Type" size="small" value={tempFilters.businessType}
                        onChange={e => setTempFilters(prev => ({ ...prev, businessType: e.target.value }))}>
                        <MenuItem value="">Any Business Type</MenuItem>
                        <MenuItem value="proprietorship">Proprietorship</MenuItem>
                        <MenuItem value="partnership">Partnership</MenuItem>
                        <MenuItem value="llp">LLP</MenuItem>
                        <MenuItem value="private_limited">Private Limited</MenuItem>
                        <MenuItem value="public_limited">Public Limited</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Status" size="small" value={tempFilters.status}
                        onChange={e => setTempFilters(prev => ({ ...prev, status: e.target.value }))}>
                        <MenuItem value="">Any Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                        <MenuItem value="verificationPending">Verification Pending</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Verified" size="small" value={tempFilters.isVerified}
                        onChange={e => setTempFilters(prev => ({ ...prev, isVerified: e.target.value }))}>
                        <MenuItem value="">Any</MenuItem>
                        <MenuItem value="true">Verified Only</MenuItem>
                        <MenuItem value="false">Unverified Only</MenuItem>
                    </TextField>

                    <TextField select fullWidth label="Deleted" size="small" value={tempFilters.isDeleted}
                        onChange={e => setTempFilters(prev => ({ ...prev, isDeleted: e.target.value }))}>
                        <MenuItem value="">No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                    </TextField>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button fullWidth variant="outlined" onClick={() => {
                            const clearedFilters = {
                                name: '', mobile: '', email: '', gender: '', provider: '',
                                customerType: '', businessType: '', status: '', isVerified: '', isDeleted: ''
                            };
                            setFilters(clearedFilters);
                            setTempFilters(clearedFilters);
                            setFilterDrawerOpen(false);
                            setMeta(prev => ({ ...prev, page: 1 }));
                        }} sx={{ color: 'grey', borderColor: 'grey' }}>Clear</Button>

                        <Button fullWidth variant="contained" onClick={() => {
                            setFilters(tempFilters);
                            setFilterDrawerOpen(false);
                            setMeta(prev => ({ ...prev, page: 1 }));
                        }} sx={{ background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 209 2)' } }}>Apply</Button>
                    </Box>
                </Box>
            </Drawer>

            {/* ── Table ────────────────────────────────────────────────── */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden'
            }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                '& th': {
                                    bgcolor: '#fafafa', fontFamily: 'Mulish, sans-serif',
                                    fontWeight: 800, fontSize: '0.72rem', textTransform: 'uppercase',
                                    letterSpacing: '0.05em', color: '#6b7280',
                                    borderBottom: '2px solid #e5e7eb', py: 1.5, whiteSpace: 'nowrap'
                                }
                            }}>
                                <TableCell>Customer</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="center">Orders</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Verified</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                    <TableCell><Skeleton height={46} /></TableCell>
                                </TableRow>
                            )) : customerList.length > 0 ? customerList.map(customer => (
                                <TableRow
                                    key={customer._id}
                                    hover
                                    sx={{
                                        '&:hover': { backgroundColor: '#f9fafb' },
                                        transition: 'background-color 0.2s ease'
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                badgeContent={<Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff', bgcolor: customer.status === 'active' ? '#10b981' : '#ef4444' }} />}>
                                                <Avatar src={customer.image} alt={customer.name} sx={{ width: 40, height: 40, bgcolor: '#ffd102', color: '#000', fontWeight: 800, fontSize: '0.85rem' }}>
                                                    {!customer.image && `${(customer.name || '')[0]}`.toUpperCase()}
                                                </Avatar>
                                            </Badge>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.9rem', color: '#111827' }}>
                                                    {highlightText(customer.name || '', filters.name || filterSearch)}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontFamily: 'Mulish, sans-serif' }}>
                                                    #{(customer._id || '').slice(-6).toUpperCase()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                <MdEmail size={13} color="#9ca3af" />
                                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#374151' }}>
                                                    {highlightText(customer.email || '', filters.email)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                <MdPhone size={13} color="#9ca3af" />
                                                <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.8rem', color: '#6b7280' }}>
                                                    {customer.mobile || '—'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={customer.customerType || "customer"} size="small"
                                            sx={{
                                                fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase',
                                                bgcolor: customer.customerType === "business" ? '#e0f2fe' : '#f3f4f6',
                                                color: customer.customerType === "business" ? '#0369a1' : '#374151', border: 'none'
                                            }} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>
                                            {customer.totalOrders || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={`Click to ${customer.status === 'active' ? 'Deactivate' : 'Activate'}`}>
                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                                <Switch checked={customer.status === 'active'} onChange={() => handleStatusToggle(customer)} size="small"
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                                                        '& .MuiSwitch-track': { bgcolor: '#ef4444' }, '& .MuiSwitch-switchBase': { color: '#ef4444' }
                                                    }} />
                                                <Typography sx={{ fontSize: '0.65rem', fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: customer.status === 'active' ? '#059669' : '#dc2626' }}>
                                                    {customer.status === 'active' ? 'Active' : 'Inactive'}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={`Click to ${customer.isVerified ? 'Unverify' : 'Verify'}`}>
                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                                <Switch checked={customer.isVerified} onChange={() => handleVerifiedToggle(customer)} size="small"
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3b82f6' },
                                                        '& .MuiSwitch-track': { bgcolor: '#9ca3af' }, '& .MuiSwitch-switchBase': { color: '#9ca3af' }
                                                    }} />
                                                <Typography sx={{ fontSize: '0.65rem', fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: customer.isVerified ? '#2563eb' : '#6b7280' }}>
                                                    {customer.isVerified ? 'Verified' : 'Unverified'}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => setViewCustomer(customer)} sx={{ bgcolor: '#eff6ff', color: '#3b82f6', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#dbeafe' } }}>
                                                    <MdVisibility size={17} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Customer">
                                                <IconButton size="small" onClick={() => handleOpenEdit(customer)} sx={{ bgcolor: '#fefce8', color: '#ca8a04', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#fef9c3' } }}>
                                                    <MdEdit size={17} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Customer">
                                                <IconButton size="small" onClick={() => handleDeleteClick(customer)} sx={{ bgcolor: '#fef2f2', color: '#dc2626', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: '#fee2e2' } }}>
                                                    <MdDeleteForever size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                            <img
                                                style={{ width: '180px' }}
                                                src='/img/nodata.png'
                                                alt='no data'
                                            />
                                            <Typography variant="h6" sx={{ fontFamily: "Mulish, sans-serif", color: "text.secondary" }}>
                                                No customers found
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid #e5e7eb' }}>
                    <Pagination
                        count={meta.totalPages}
                        page={meta.page}
                        onChange={handlePageChange}
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        sx={{
                            '& .MuiPaginationItem-root': {
                                fontFamily: 'Mulish, sans-serif',
                                '&.Mui-selected': { bgcolor: '#ffd102', color: '#000' }
                            }
                        }}
                    />
                </Box>
            </Box>

            {meta.total > 0 && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
                </Typography>
            )}

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <img src="./img/del1.png" alt="delete" style={{ width: 80 }} />
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ textAlign: 'center', fontSize: 16 }}>
                        Delete <strong>{customerToDelete?.name}</strong> permanently?
                    </DialogContentText>
                    <Box sx={{ backgroundColor: '#fff3cd', p: 2, borderRadius: 1, borderLeft: '4px solid #ffc107', mt: 2 }}>
                        <Typography fontWeight="bold" color="#856404">Warning</Typography>
                        <Typography color="#856404">This action cannot be undone.</Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">Yes, Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog open={addCustomerOpen} onClose={handleCloseAddCustomer} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <IconButton onClick={handleCloseAddCustomer}><FaArrowLeft /></IconButton>
                        <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                            {editMode ? 'Update Customer' : 'Add New Customer'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Name" name="name" value={newCustomer.name} onChange={handleInputChange}
                                error={!!errors.name} helperText={errors.name} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" name="email" type="email" value={newCustomer.email} onChange={handleInputChange}
                                error={!!errors.email} helperText={errors.email} required />
                        </Grid>
                        {!editMode && (
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Password" name="password" type="password" value={newCustomer.password}
                                    onChange={handleInputChange} error={!!errors.password}
                                    helperText={errors.password || 'Min 6 characters'} required />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth label="Gender" name="gender" value={newCustomer.gender} onChange={handleInputChange}>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Mobile Number" name="mobile" value={newCustomer.mobile} onChange={handleInputChange}
                                error={!!errors.mobile} helperText={errors.mobile || '10 digits only'} required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Verification Status"
                                name="isVerified"
                                value={newCustomer.isVerified ? "true" : "false"}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="true">Verified</MenuItem>
                                <MenuItem value="false">Unverified</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Customer Type"
                                name="customerType"
                                value={newCustomer.customerType}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="customer">Customer</MenuItem>
                                <MenuItem value="business">Business</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button variant="outlined" component="label" fullWidth sx={{ height: 56, borderColor: errors.image ? 'error.main' : undefined }}>
                                {newCustomer.image ? newCustomer.image.name : 'Choose Image *'}
                                <input type="file" hidden accept="image/*" name="image" onChange={handleInputChange} />
                            </Button>
                            {errors.image && <Typography color="error" variant="caption" sx={{ ml: 1.5, mt: 0.5 }}>{errors.image}</Typography>}
                        </Grid>
                        {newCustomer.imagePreview && (
                            <Grid item xs={12}>
                                <Box textAlign="center" mt={2}>
                                    <img src={newCustomer.imagePreview} alt="Preview"
                                        style={{ maxHeight: 180, borderRadius: 8, border: '1px solid #ddd' }} />
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {formError && <Alert severity="error" sx={{ mt: 3 }}>{formError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button sx={{ color: "grey" }} onClick={handleCloseAddCustomer}>Cancel</Button>
                    <Button onClick={handleAddCustomer} variant="contained"
                        sx={{ background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 209 2)' }, border: "1px solid black" }}>
                        {editMode ? 'Update Customer' : 'Add Customer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Details Drawer */}
            <Drawer anchor="right" open={!!viewCustomer} onClose={() => setViewCustomer(null)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, p: 0 } }}>
                {viewCustomer && (
                    <>
                        {/* Header */}
                        <Box sx={{ bgcolor: '#111827', p: 3, pb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <IconButton onClick={() => setViewCustomer(null)}
                                    sx={{ color: '#9ca3af', '&:hover': { color: '#fff' } }}>
                                    <MdClear size={20} />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                <Avatar src={viewCustomer.image} alt={viewCustomer.name}
                                    sx={{
                                        width: 80, height: 80, bgcolor: '#ffd102', color: '#000',
                                        fontWeight: 900, fontSize: '1.8rem',
                                        border: '4px solid rgba(255,255,255,0.15)'
                                    }}>
                                    {!viewCustomer.image && `${(viewCustomer.name || '')[0]}`.toUpperCase()}
                                </Avatar>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#fff', fontSize: '1.2rem' }}>
                                        {viewCustomer.name}
                                    </Typography>
                                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#9ca3af', fontSize: '0.85rem' }}>
                                        #{viewCustomer._id?.slice(-8).toUpperCase()}
                                    </Typography>
                                    <Chip label={viewCustomer.customerType || 'customer'} size="small"
                                        sx={{ mt: 0.5, fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.72rem', bgcolor: 'rgba(255,209,2,0.15)', color: '#ffd102', textTransform: 'uppercase' }} />
                                </Box>

                                {/* Inline status & verified toggles in drawer */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.08)', px: 2, py: 1, borderRadius: 2 }}>
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#9ca3af' }}>Status:</Typography>
                                        <Switch checked={viewCustomer.status === 'active'} onChange={() => handleStatusToggle(viewCustomer)} size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                                                '& .MuiSwitch-track': { bgcolor: '#ef4444' },
                                                '& .MuiSwitch-switchBase': { color: '#ef4444' }
                                            }} />
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: viewCustomer.status === 'active' ? '#10b981' : '#ef4444' }}>
                                            {viewCustomer.status === 'active' ? 'Active' : 'Inactive'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.08)', px: 2, py: 1, borderRadius: 2 }}>
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#9ca3af' }}>Verified:</Typography>
                                        <Switch checked={!!viewCustomer.isVerified} onChange={() => handleVerifiedToggle(viewCustomer)} size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3b82f6' },
                                                '& .MuiSwitch-track': { bgcolor: '#9ca3af' },
                                                '& .MuiSwitch-switchBase': { color: '#9ca3af' }
                                            }} />
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: viewCustomer.isVerified ? '#3b82f6' : '#9ca3af' }}>
                                            {viewCustomer.isVerified ? 'Verified' : 'Unverified'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        {/* Details */}
                        <Box sx={{ p: 3, overflowY: 'auto' }}>
                            {[
                                { label: 'Email Address', value: viewCustomer.email, icon: <MdEmail size={16} /> },
                                { label: 'Phone Number', value: viewCustomer.mobile, icon: <MdPhone size={16} /> },
                                { label: 'Total Orders', value: viewCustomer.totalOrders || 0, icon: <FiActivity size={16} /> },
                                { label: 'Business Type', value: viewCustomer.businessType || '—', icon: <MdBusiness size={16} /> },
                                { label: 'Joined', value: viewCustomer.createdAt ? new Date(viewCustomer.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '—', icon: null },
                            ].map((row, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {row.icon && <Box sx={{ color: '#9ca3af' }}>{row.icon}</Box>}
                                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.82rem', color: '#6b7280' }}>{row.label}</Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#111827', maxWidth: '55%', textAlign: 'right', wordBreak: 'break-word' }}>
                                        {row.value || '—'}
                                    </Typography>
                                </Box>
                            ))}

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" spacing={1.5}>
                                <Button fullWidth variant="outlined" startIcon={<MdEdit size={16} />} onClick={() => { setViewCustomer(null); handleOpenEdit(viewCustomer); }}
                                    sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, borderRadius: '10px', borderColor: '#ffd102', color: '#92400e', '&:hover': { bgcolor: '#fffbeb', borderColor: '#f59e0b' } }}>
                                    Edit
                                </Button>
                                <Button fullWidth variant="outlined" startIcon={<MdDeleteForever size={16} />} onClick={() => { setViewCustomer(null); handleDeleteClick(viewCustomer); }}
                                    sx={{ fontFamily: 'Mulish, sans-serif', textTransform: 'none', fontWeight: 700, borderRadius: '10px', borderColor: '#fca5a5', color: '#dc2626', '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' } }}>
                                    Delete
                                </Button>
                            </Stack>
                        </Box>
                    </>
                )}
            </Drawer>
        </Box>
    );
}

const SimpleRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f5f5f5', pb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={600}>{value || '-'}</Typography>
    </Box>
);

export default Customermanagement;